import React, { useEffect, useState, useCallback, useRef } from "react";
import { useIsTouch } from "../hooks/useMediaQuery";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faRobot, faSpinner, faTriangleExclamation, faCircleCheck,
  faCircleXmark, faUser, faCodeBranch, faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import api from "../services/api";
import styles from "./AiUsageModal.module.css";
import ModalHeader from "./ModalHeader";
import { getCssVar } from "../utils/theme";
import { dateToLocalISO } from "../utils/date";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip);

const PRESET_RANGES = [7, 30, 90];

function subtractDays(days) {
  const d = new Date();
  d.setDate(d.getDate() - days + 1);
  return dateToLocalISO(d);
}

function today() { return dateToLocalISO(new Date()); }

// ── Formatação (pt-BR) ──────────────────────────────────────────────────────
const nf = (opts) => new Intl.NumberFormat("pt-BR", opts);

function fmtUsd(v) {
  const n = Number(v || 0);
  // centavos de dólar somem em 2 casas: abaixo de US$ 0,01 mostra 4
  const digits = n > 0 && n < 0.01 ? 4 : 2;
  return `US$ ${nf({ minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n)}`;
}

function fmtInt(n) { return nf({ maximumFractionDigits: 0 }).format(Number(n || 0)); }

function fmtPct(n) { return `${nf({ maximumFractionDigits: 1 }).format(Number(n || 0))}%`; }

function fmtSec(ms) {
  if (ms == null) return "-";
  if (ms < 1000) return `${fmtInt(ms)} ms`;
  return `${nf({ maximumFractionDigits: 1 }).format(ms / 1000)} s`;
}

function fmtTokens(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${nf({ maximumFractionDigits: 1 }).format(v / 1_000_000)} mi`;
  if (v >= 1000) return `${nf({ maximumFractionDigits: 1 }).format(v / 1000)} mil`;
  return fmtInt(v);
}

/** "2026-10-13" -> "13/10" (eixo) ou "13/10/2026" (completo) */
function fmtDay(iso, full = false) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return full ? `${d}/${m}/${y}` : `${d}/${m}`;
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short",
  });
}

// Taxa de erro: estado com ícone + rótulo, nunca só cor
function errorStatus(pct) {
  if (pct > 10) return { label: "Alto", icon: faCircleXmark, cls: styles.statusCritical };
  if (pct > 3) return { label: "Atenção", icon: faTriangleExclamation, cls: styles.statusWarning };
  return { label: "Normal", icon: faCircleCheck, cls: styles.statusGood };
}

function KpiCard({ label, value, sub, children }) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiVal}>{value}</div>
      {sub && <div className={styles.kpiSub}>{sub}</div>}
      {children}
    </div>
  );
}

function ChartCard({ title, summary, children, empty }) {
  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>{title}</h3>
      <div className={styles.chartArea} role="img" aria-label={summary}>
        {empty ? <div className={styles.noData}>Sem chamadas no período</div> : children}
      </div>
    </div>
  );
}

/** Cores do Chart.js resolvidas na hora: o canvas não entende var(--token). */
function chartTheme() {
  return {
    accent: getCssVar("--blue", "#1d9e75"),
    ink: getCssVar("--text-0-60", "rgba(225,245,238,0.6)"),
    grid: getCssVar("--border-0-06", "rgba(255,255,255,0.06)"),
  };
}

function baseOptions(t, { tooltip, yTick, horizontal = false, touch }) {
  const valueAxis = {
    beginAtZero: true,
    grid: { color: t.grid, drawTicks: false },
    border: { display: false },
    ticks: { color: t.ink, font: { size: 11 }, padding: 6, maxTicksLimit: 5, callback: yTick },
  };
  const catAxis = {
    grid: { display: false },
    border: { color: t.grid },
    ticks: { color: t.ink, font: { size: 11 }, autoSkip: true, maxRotation: 0, autoSkipPadding: 12 },
  };
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? "y" : "x",
    animation: { duration: 250 },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: !touch, displayColors: false, padding: 10, callbacks: tooltip },
    },
    scales: horizontal ? { x: valueAxis, y: catAxis } : { x: catAxis, y: valueAxis },
  };
}

export default function AiUsageModal({ open, onClose }) {
  useBodyScrollLock(open);
  // Tooltip de toque some do jeito errado e polui em telas pequenas — mesmo
  // criterio usado nos outros graficos (ver docs/07-issues.md). Os valores
  // continuam acessíveis pela tabela por dia e pelas tabelas de detalhe.
  //
  // Declarado AQUI, no topo: mais abaixo existe um `if (!open) return null`, e
  // hook depois de saida antecipada quebra a regra de ordem dos hooks.
  const isTouchDevice = useIsTouch();
  const [preset, setPreset] = useState(30);
  const [startDate, setStartDate] = useState(subtractDays(30));
  const [endDate, setEndDate] = useState(today());
  const [selectedUserId, setSelectedUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);

  const load = useCallback(async (sd, ed, uid) => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const params = { start_date: sd, end_date: ed };
      if (uid) params.user_id = uid;
      const res = await api.get("/ai/usage-stats", { params });
      if (reqId !== requestIdRef.current) return; // resposta obsoleta — ignora
      setData(res.data);
      // Atualiza lista de usuários apenas quando sem filtro de usuário (lista completa do período)
      if (!uid && res.data.users) setUsers(res.data.users);
    } catch (e) {
      if (reqId !== requestIdRef.current) return;
      setError(e?.response?.data?.detail || "Erro ao carregar dados");
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedUserId("");
      load(startDate, endDate, "");
    }
  }, [open]); // eslint-disable-line

  function applyPreset(days) {
    setPreset(days);
    const sd = subtractDays(days);
    const ed = today();
    setStartDate(sd);
    setEndDate(ed);
    setSelectedUserId("");
    setUsers([]);
    load(sd, ed, "");
  }

  function applyCustom() {
    setPreset(null);
    setSelectedUserId("");
    setUsers([]);
    load(startDate, endDate, "");
  }

  function handleUserChange(e) {
    const uid = e.target.value;
    setSelectedUserId(uid);
    load(startDate, endDate, uid);
  }

  if (!open) return null;

  const kpis = data?.kpis;
  const series = data?.daily_series || [];
  const byEndpoint = data?.by_endpoint || [];
  const byModel = data?.by_model || [];
  const byFlow = data?.by_flow || [];
  const topRetryReasons = data?.top_retry_reasons || [];
  const recentErrors = data?.recent_errors || [];
  const hasCalls = (kpis?.total_calls || 0) > 0;
  const labelOf = (ep) => ep.label || ep.endpoint;

  const t = chartTheme();
  const dayLabels = series.map((d) => fmtDay(d.date));
  const bar = { backgroundColor: t.accent, borderRadius: 4, maxBarThickness: 18, categoryPercentage: 0.8, barPercentage: 0.9 };
  const dayTitle = (items) => fmtDay(series[items[0].dataIndex]?.date, true);

  // Custo por dia
  const costData = { labels: dayLabels, datasets: [{ ...bar, data: series.map((d) => d.cost_usd) }] };
  const costOpts = baseOptions(t, {
    touch: isTouchDevice,
    yTick: (v) => fmtUsd(v),
    tooltip: {
      title: dayTitle,
      label: (ctx) => {
        const d = series[ctx.dataIndex];
        return [`Custo: ${fmtUsd(d.cost_usd)}`, `${fmtInt(d.calls)} ${d.calls === 1 ? "chamada" : "chamadas"}`];
      },
    },
  });

  // Chamadas por dia: uma série só (o total já inclui os erros). Erro vai no
  // tooltip e no card de status; empilhar "chamadas + erros" contava o erro
  // duas vezes.
  const callsData = { labels: dayLabels, datasets: [{ ...bar, data: series.map((d) => d.calls) }] };
  const callsOpts = baseOptions(t, {
    touch: isTouchDevice,
    yTick: (v) => (Number.isInteger(v) ? fmtInt(v) : ""),
    tooltip: {
      title: dayTitle,
      label: (ctx) => {
        const d = series[ctx.dataIndex];
        const lines = [`${fmtInt(d.calls)} ${d.calls === 1 ? "chamada" : "chamadas"}`];
        if (d.errors) lines.push(`${fmtInt(d.errors)} com erro`);
        return lines;
      },
    },
  });

  // Custo por recurso (barra horizontal, do mais caro pro mais barato)
  const epData = {
    labels: byEndpoint.map(labelOf),
    datasets: [{ ...bar, maxBarThickness: 16, data: byEndpoint.map((e) => e.cost_usd) }],
  };
  const epOpts = baseOptions(t, {
    touch: isTouchDevice,
    horizontal: true,
    yTick: (v) => fmtUsd(v),
    tooltip: {
      label: (ctx) => {
        const e = byEndpoint[ctx.dataIndex];
        return [`Custo: ${fmtUsd(e.cost_usd)}`, `${fmtInt(e.calls)} chamadas`];
      },
    },
  });

  // Tempo médio de resposta (s). Dia sem chamada fica sem ponto (null), não 0.
  const rtData = {
    labels: dayLabels,
    datasets: [{
      data: series.map((d) => (d.avg_response_ms == null ? null : d.avg_response_ms / 1000)),
      borderColor: t.accent,
      backgroundColor: t.accent,
      borderWidth: 2,
      tension: 0,
      spanGaps: false,
      pointRadius: 3,
      pointHoverRadius: 5,
    }],
  };
  const rtOpts = baseOptions(t, {
    touch: isTouchDevice,
    yTick: (v) => `${nf({ maximumFractionDigits: 1 }).format(v)} s`,
    tooltip: {
      title: dayTitle,
      label: (ctx) => (ctx.raw == null ? "Sem chamadas" : `Tempo médio: ${fmtSec(series[ctx.dataIndex].avg_response_ms)}`),
    },
  });

  const status = kpis ? errorStatus(kpis.error_rate_pct) : null;
  const costPerCall = kpis && kpis.total_calls ? kpis.total_cost_usd / kpis.total_calls : 0;
  const topEndpoint = byEndpoint[0];
  const peakCostDay = series.reduce((best, d) => (d.cost_usd > (best?.cost_usd || 0) ? d : best), null);
  const peakCallsDay = series.reduce((best, d) => (d.calls > (best?.calls || 0) ? d : best), null);

  const content = (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ai-usage-title">
        <ModalHeader onClose={onClose} />

        <div className={styles.header}>
          <FontAwesomeIcon icon={faRobot} className={styles.headerIcon} aria-hidden="true" />
          <h2 id="ai-usage-title" className={styles.headerTitle}>Consumo de IA</h2>
        </div>

        {/* Filtros: uma linha só, valem pra tudo abaixo */}
        <div className={styles.controls}>
          <div className={styles.presetGroup} role="group" aria-label="Período">
            {PRESET_RANGES.map(days => (
              <button
                key={days}
                type="button"
                className={styles.presetBtn}
                aria-pressed={preset === days}
                onClick={() => applyPreset(days)}
              >
                {days} dias
              </button>
            ))}
          </div>
          <div className={styles.customRange}>
            <input type="date" className={styles.dateInput} value={startDate} aria-label="Data inicial"
                   onChange={e => setStartDate(e.target.value)} />
            <span className={styles.dateLabel}>até</span>
            <input type="date" className={styles.dateInput} value={endDate} aria-label="Data final"
                   onChange={e => setEndDate(e.target.value)} />
            <button type="button" className={styles.applyBtn} onClick={applyCustom} disabled={loading}>
              Aplicar
            </button>
          </div>
          <div className={styles.userFilter}>
            <FontAwesomeIcon icon={faUser} className={styles.userFilterIcon} aria-hidden="true" />
            <select
              className={styles.userSelect}
              value={selectedUserId}
              onChange={handleUserChange}
              disabled={loading}
              aria-label="Filtrar por usuário"
            >
              <option value="">Todos os usuários</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            {selectedUserId && (
              <button
                type="button"
                className={styles.clearUserBtn}
                onClick={() => { setSelectedUserId(""); load(startDate, endDate, ""); }}
                aria-label="Limpar filtro de usuário"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          {loading && <FontAwesomeIcon icon={faSpinner} spin className={styles.inlineSpinner} aria-label="Carregando" />}
        </div>

        {data?.period && (
          <p className={styles.periodNote}>
            {fmtDay(data.period.start_date, true)} a {fmtDay(data.period.end_date, true)}
            {selectedUserId && users.length > 0 && (
              <> • {users.find(u => String(u.id) === String(selectedUserId))?.name}</>
            )}
          </p>
        )}

        {error && (
          <div className={styles.errorBanner} role="alert">
            <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
          </div>
        )}

        {loading && !data && (
          <div className={styles.loadingWrap}>
            <FontAwesomeIcon icon={faSpinner} spin className={styles.bigSpinner} aria-label="Carregando" />
          </div>
        )}

        {data && !hasCalls && (
          <div className={styles.emptyState}>Nenhuma chamada de IA registrada neste período.</div>
        )}

        {/* Recarregando: mantém o que já estava na tela, esmaecido (sem pulo de layout) */}
        {data && hasCalls && (
          <div className={loading ? styles.stale : undefined} aria-busy={loading}>
            <div className={styles.kpiGrid}>
              <KpiCard label="Custo no período" value={fmtUsd(kpis.total_cost_usd)}
                       sub={`${fmtUsd(costPerCall)} por chamada`} />
              <KpiCard label="Chamadas" value={fmtInt(kpis.total_calls)}
                       sub={`${fmtPct(kpis.retry_rate_pct)} precisaram de nova tentativa`} />
              <KpiCard label="Taxa de erro" value={fmtPct(kpis.error_rate_pct)}
                       sub={`${fmtInt(kpis.total_errors)} de ${fmtInt(kpis.total_calls)} chamadas`}>
                <span className={`${styles.statusChip} ${status.cls}`}>
                  <FontAwesomeIcon icon={status.icon} aria-hidden="true" /> {status.label}
                </span>
              </KpiCard>
              <KpiCard label="Tempo de resposta (P95)" value={fmtSec(kpis.p95_response_ms)}
                       sub={`média de ${fmtSec(kpis.avg_response_ms)}`} />
              <KpiCard label="Tokens" value={fmtTokens(kpis.total_input_tokens + kpis.total_output_tokens)}
                       sub={`entrada ${fmtTokens(kpis.total_input_tokens)} • saída ${fmtTokens(kpis.total_output_tokens)}`} />
              <KpiCard label="Economia com cache" value={fmtUsd(kpis.cache_savings_usd)}
                       sub={`${fmtTokens(kpis.total_cache_read_tokens)} tokens reaproveitados`} />
            </div>

            <div className={styles.chartGrid}>
              <ChartCard
                title="Custo por dia"
                summary={peakCostDay ? `Custo por dia. Maior custo em ${fmtDay(peakCostDay.date, true)}: ${fmtUsd(peakCostDay.cost_usd)}.` : "Custo por dia."}
              >
                <Bar data={costData} options={costOpts} />
              </ChartCard>
              <ChartCard
                title="Custo por recurso"
                summary={topEndpoint ? `Custo por recurso. Maior: ${labelOf(topEndpoint)}, ${fmtUsd(topEndpoint.cost_usd)}.` : "Custo por recurso."}
                empty={!byEndpoint.length}
              >
                <Bar data={epData} options={epOpts} />
              </ChartCard>
              <ChartCard
                title="Chamadas por dia"
                summary={peakCallsDay ? `Chamadas por dia. Pico em ${fmtDay(peakCallsDay.date, true)}: ${fmtInt(peakCallsDay.calls)} chamadas.` : "Chamadas por dia."}
              >
                <Bar data={callsData} options={callsOpts} />
              </ChartCard>
              <ChartCard
                title="Tempo médio de resposta"
                summary={`Tempo médio de resposta por dia. Média do período: ${fmtSec(kpis.avg_response_ms)}.`}
              >
                <Line data={rtData} options={rtOpts} />
              </ChartCard>
            </div>

            {/* Tabela equivalente aos gráficos por dia (acessível e útil pra conferir) */}
            <details className={styles.tableCard}>
              <summary className={styles.detailsSummary}>Ver os números por dia</summary>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col">Dia</th>
                      <th scope="col" className={styles.num}>Chamadas</th>
                      <th scope="col" className={styles.num}>Erros</th>
                      <th scope="col" className={styles.num}>Custo</th>
                      <th scope="col" className={styles.num}>Tempo médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {series.filter(d => d.calls > 0).reverse().map(d => (
                      <tr key={d.date}>
                        <td>{fmtDay(d.date, true)}</td>
                        <td className={styles.num}>{fmtInt(d.calls)}</td>
                        <td className={styles.num}>{fmtInt(d.errors)}</td>
                        <td className={styles.num}>{fmtUsd(d.cost_usd)}</td>
                        <td className={styles.num}>{fmtSec(d.avg_response_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            {byEndpoint.length > 0 && (
              <section className={styles.tableCard}>
                <h3 className={styles.chartTitle}>Detalhe por recurso</h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Recurso</th>
                        <th scope="col" className={styles.num}>Chamadas</th>
                        <th scope="col" className={styles.num}>Erros</th>
                        <th scope="col" className={styles.num}>Tokens</th>
                        <th scope="col" className={styles.num}>Custo</th>
                        <th scope="col" className={styles.num}>Por chamada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byEndpoint.map(ep => (
                        <tr key={ep.endpoint}>
                          <td>{labelOf(ep)}</td>
                          <td className={styles.num}>{fmtInt(ep.calls)}</td>
                          <td className={styles.num}>
                            {ep.errors > 0 ? `${fmtInt(ep.errors)} (${fmtPct(ep.errors / ep.calls * 100)})` : "0"}
                          </td>
                          <td className={styles.num}>{fmtTokens(ep.tokens)}</td>
                          <td className={styles.num}>{fmtUsd(ep.cost_usd)}</td>
                          <td className={styles.num}>{fmtUsd(ep.calls ? ep.cost_usd / ep.calls : 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {byFlow.length > 1 && (
              <section className={styles.tableCard}>
                <h3 className={styles.chartTitle}>
                  <FontAwesomeIcon icon={faCodeBranch} aria-hidden="true" /> Por fluxo (professor / por conta própria)
                </h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Fluxo</th>
                        <th scope="col" className={styles.num}>Chamadas</th>
                        <th scope="col" className={styles.num}>Tokens</th>
                        <th scope="col" className={styles.num}>Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byFlow.map(f => (
                        <tr key={f.flow}>
                          <td>{f.flow_label}</td>
                          <td className={styles.num}>{fmtInt(f.calls)}</td>
                          <td className={styles.num}>{fmtTokens(f.tokens)}</td>
                          <td className={styles.num}>{fmtUsd(f.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {byModel.length > 0 && (
              <section className={styles.tableCard}>
                <h3 className={styles.chartTitle}>Modelos utilizados</h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Modelo</th>
                        <th scope="col" className={styles.num}>Chamadas</th>
                        <th scope="col" className={styles.num}>Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byModel.map(m => (
                        <tr key={m.model}>
                          <td><code className={styles.modelCode}>{m.model}</code></td>
                          <td className={styles.num}>{fmtInt(m.calls)}</td>
                          <td className={styles.num}>{fmtUsd(m.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {topRetryReasons.length > 0 && (
              <section className={styles.tableCard}>
                <h3 className={styles.chartTitle}>
                  <FontAwesomeIcon icon={faListCheck} aria-hidden="true" /> Motivos mais comuns de nova tentativa
                </h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Motivo (agrupado)</th>
                        <th scope="col" className={styles.num}>Vezes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRetryReasons.map((r, idx) => (
                        <tr key={idx}>
                          <td className={styles.wrapText}>{r.reason}</td>
                          <td className={styles.num}>{fmtInt(r.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {recentErrors.length > 0 && (
              <section className={styles.tableCard}>
                <h3 className={styles.chartTitle}>
                  <FontAwesomeIcon icon={faCircleXmark} aria-hidden="true" /> Erros recentes
                </h3>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Recurso</th>
                        <th scope="col">Mensagem</th>
                        <th scope="col" className={styles.num}>Tempo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentErrors.map(e => (
                        <tr key={e.id}>
                          <td className={styles.noWrap}>{fmtDateTime(e.created_at)}</td>
                          <td className={styles.noWrap}>{e.label || e.endpoint}</td>
                          <td className={styles.errMsg} title={e.error_message || ""}>{e.error_message || "-"}</td>
                          <td className={styles.num}>{fmtSec(e.response_time_ms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
